import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Título, descripción, canonical y robots de cada página.
 *
 * OJO — esto NO es lo que lee Google. El HTML que reciben los buscadores y los
 * previsualizadores de enlaces (WhatsApp, Instagram, Facebook, que no ejecutan
 * JavaScript) lo compone el backend en
 * BackEnd/internal/controllers/seo_render_controller.go, que inyecta estas
 * mismas etiquetas en el <head> antes de servir el index.html.
 *
 * Este módulo cubre lo que aquello no puede: la navegación interna de la SPA,
 * donde no hay petición al servidor y el <title> se quedaría con el de la
 * página anterior. Sirve para la pestaña del navegador, para el historial y
 * para que la analítica registre la página correcta.
 *
 * Si se cambia un texto aquí, hay que cambiarlo también allí, y al revés.
 */

const NOMBRE_SITIO = 'Tablero Infinito';
const DESCRIPCION_HOME =
  'Tienda de juegos de mesa en Colombia. Compra juegos de mesa, cartas y party games originales con envío a todo el país.';

const RUTAS = {
  '/': {
    titulo: `${NOMBRE_SITIO} | Juegos de mesa en Colombia`,
    descripcion: DESCRIPCION_HOME,
  },
  '/productos': {
    titulo: `Juegos de mesa disponibles | ${NOMBRE_SITIO}`,
    descripcion:
      'Todo el catálogo de juegos de mesa de Tablero Infinito: party games, juegos de cartas, familiares y de estrategia, con envío a toda Colombia.',
  },
  '/ahilesdejo': {
    titulo: `Ahí les dejo | Juego de cartas colombiano | ${NOMBRE_SITIO}`,
    descripcion:
      'Ahí les dejo, el juego de cartas colombiano de Tablero Infinito. Para reírse con amigos y familia.',
  },
  '/politica-privacidad': {
    titulo: `Política de privacidad | ${NOMBRE_SITIO}`,
    descripcion: 'Cómo tratamos tus datos personales en Tablero Infinito.',
  },
  '/terminos-condiciones': {
    titulo: `Términos y condiciones | ${NOMBRE_SITIO}`,
    descripcion: 'Términos y condiciones de compra en Tablero Infinito.',
  },
};

// Páginas con estado del visitante o del panel: nunca deben indexarse. Va por
// prefijo porque casi todas tienen subrutas.
const PREFIJOS_NO_INDEXABLES = [
  '/admin',
  '/carrito',
  '/checkout',
  '/perfil',
  '/pedidos',
  '/mis-pagos',
  '/login',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/payment-result',
  '/seguimiento',
];

function noIndexable(pathname) {
  return PREFIJOS_NO_INDEXABLES.some(p => pathname === p || pathname.startsWith(`${p}/`));
}

function metadatosDeRuta(pathname, search) {
  // Una búsqueda es otra vista del mismo catálogo, no una página nueva: se
  // deja seguir sus enlaces pero no se indexa el listado.
  if (pathname === '/productos') {
    const termino = new URLSearchParams(search).get('search');
    if (termino) {
      return {
        titulo: `Resultados para «${termino}» | ${NOMBRE_SITIO}`,
        descripcion: RUTAS['/productos'].descripcion,
        robots: 'noindex, follow',
      };
    }
  }

  const base = RUTAS[pathname];
  if (base) return base;

  return {
    titulo: NOMBRE_SITIO,
    descripcion: DESCRIPCION_HOME,
    robots: noIndexable(pathname) ? 'noindex, follow' : undefined,
  };
}

/** Crea la etiqueta si no existe y le pone el valor; la quita si no hay valor. */
function fijarMeta(selector, crear, valor) {
  let etiqueta = document.head.querySelector(selector);

  if (!valor) {
    if (etiqueta) etiqueta.remove();
    return;
  }

  if (!etiqueta) {
    etiqueta = crear();
    document.head.appendChild(etiqueta);
  }
  etiqueta.setAttribute(selector.startsWith('link') ? 'href' : 'content', valor);
}

function aplicar({ titulo, descripcion, robots, canonical }) {
  if (titulo) document.title = titulo;

  fijarMeta(
    'meta[name="description"]',
    () => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'description');
      return m;
    },
    descripcion
  );

  fijarMeta(
    'meta[name="robots"]',
    () => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'robots');
      return m;
    },
    robots
  );

  fijarMeta(
    'link[rel="canonical"]',
    () => {
      const l = document.createElement('link');
      l.setAttribute('rel', 'canonical');
      return l;
    },
    canonical
  );
}

// El valor por defecto solo se usa si alguien llama a useSeo fuera del
// proveedor; no hace nada porque en ese caso no hay cabecera que mantener.
function sinProveedor() {
  return undefined;
}

const SeoContext = createContext(sinProveedor);

/**
 * Mantiene la cabecera al día durante la navegación interna.
 *
 * El componente que publica metadatos es siempre el proveedor, nunca la página:
 * en React los efectos de los hijos corren antes que los del padre, así que si
 * cada página escribiera en document.title directamente, el proveedor la
 * pisaría al reaccionar al cambio de ruta.
 */
export function SeoProvider({ children }) {
  const { pathname, search } = useLocation();
  const [publicado, setPublicado] = useState(null);

  const metadatos = useMemo(() => {
    const base = metadatosDeRuta(pathname, search);

    // El canonical descarta la cadena de consulta a propósito:
    // CampaignParamsKeeper añade utm_* y ti=<click_id> en cada navegación, de
    // modo que la misma página circula con muchas direcciones distintas.
    const canonical = `${window.location.origin}${pathname}`;

    // Lo publicado por una página solo vale para SU ruta: se compara en vez de
    // limpiarlo al cambiar de ruta, porque el efecto de limpieza correría
    // después del de la página y borraría lo que acaba de publicar.
    if (publicado && publicado.pathname === pathname) {
      return { ...base, ...publicado, canonical };
    }
    return { ...base, canonical };
  }, [pathname, search, publicado]);

  useEffect(() => {
    aplicar(metadatos);
  }, [metadatos]);

  return <SeoContext.Provider value={setPublicado}>{children}</SeoContext.Provider>;
}

/**
 * Permite a una página aportar lo que solo ella sabe (el nombre del juego, por
 * ejemplo). Se ignora mientras `titulo` sea nulo, que es lo que ocurre mientras
 * la ficha está cargando.
 */
export function useSeo({ titulo, descripcion, robots } = {}) {
  const publicar = useContext(SeoContext);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!titulo) return;
    publicar({ pathname, titulo, descripcion, robots });
  }, [publicar, pathname, titulo, descripcion, robots]);
}

export default SeoProvider;
